/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback, useEffect, useState } from "react";
import { Toaster } from "../components/toaster/ThemedToaster";
/** a React hook for managing an error state */
export const useErrorManager = (_a = {}) => {
    var { t, eventTracker } = _a, managerOpts = __rest(_a, ["t", "eventTracker"]);
    useEffect(() => {
        if ((managerOpts.trackErrorEvent || managerOpts.trackSuccessEvent) &&
            !eventTracker) {
            console.warn("useErrorManager: eventTracker must be provided along trackErrorEvent or trackSuccessEvent");
        }
    }, [
        managerOpts.trackErrorEvent,
        managerOpts.trackSuccessEvent,
        eventTracker,
    ]);
    useEffect(() => {
        if (managerOpts.toastMessageTKey && !t) {
            console.warn("useErrorManager: t must be provided along toastMessageTKey");
        }
    }, [managerOpts.toastMessageTKey, t]);
    const [fatalError, setFatalError] = useState();
    const throwFatalError = (arg) => {
        if (arg instanceof Error) {
            setFatalError(arg);
        }
        else {
            setFatalError(new Error(arg));
        }
    };
    /** post a return (aka success) toast */
    const returnToast = useCallback((returnOpts = {}) => {
        var _a, _b, _c, _d;
        if (returnOpts.messageTKey && !t) {
            console.warn("useErrorManager: t must be provided at hook initialisation when providing returnToast with messageTKey");
        }
        const messageTKey = (_a = returnOpts.messageTKey) !== null && _a !== void 0 ? _a : managerOpts.toastMessageTKey;
        if (messageTKey && t) {
            const linkProps = managerOpts.toastLink &&
                // either callback or url must be defined
                (managerOpts.toastLink.callback || managerOpts.toastLink.url)
                ? managerOpts.toastLink
                : undefined;
            Toaster.success(t(messageTKey, returnOpts.translationCtx), {
                duration: managerOpts.toastDuration,
                hasCloseButton: managerOpts.toastClosable !== undefined
                    ? managerOpts.toastClosable
                    : true,
                type: (_b = managerOpts.toastPermanence) !== null && _b !== void 0 ? _b : "temporary",
                link: linkProps && {
                    title: linkProps.text,
                    url: (_d = (_c = linkProps.callback) !== null && _c !== void 0 ? _c : linkProps.url) !== null && _d !== void 0 ? _d : "", // "" is unreachable
                },
            });
        }
        if (managerOpts.trackSuccessEvent && eventTracker) {
            eventTracker(managerOpts.trackSuccessEvent, returnOpts.telemetryCtx);
        }
    }, [managerOpts, t, eventTracker]);
    const throwToast = useCallback((error, throwOpts = {}) => {
        var _a, _b, _c, _d;
        if (throwOpts.messageTKey && !t) {
            console.warn("useErrorManager: t must be provided at hook initialisation when providing throwToast with messageTKey");
        }
        console.error(error);
        const messageTKey = (_a = throwOpts.messageTKey) !== null && _a !== void 0 ? _a : managerOpts.toastMessageTKey;
        if (messageTKey && t) {
            const linkProps = managerOpts.toastLink &&
                // either callback or url must be defined
                (managerOpts.toastLink.callback || managerOpts.toastLink.url)
                ? managerOpts.toastLink
                : undefined;
            Toaster.error(t(messageTKey, throwOpts.translationCtx), {
                duration: managerOpts.toastDuration,
                hasCloseButton: managerOpts.toastClosable !== undefined
                    ? managerOpts.toastClosable
                    : true,
                type: (_b = managerOpts.toastPermanence) !== null && _b !== void 0 ? _b : "temporary",
                link: linkProps && {
                    title: linkProps.text,
                    url: (_d = (_c = linkProps.callback) !== null && _c !== void 0 ? _c : linkProps.url) !== null && _d !== void 0 ? _d : "", // "" is unreachable
                },
            });
        }
        if (managerOpts.trackErrorEvent && eventTracker) {
            eventTracker(managerOpts.trackErrorEvent, throwOpts.telemetryCtx);
        }
    }, [managerOpts, t, eventTracker]);
    const catch_ = useCallback((promise, opts = {}) => {
        promise.catch((err) => throwToast(err, opts));
    }, [throwToast]);
    return {
        fatalError,
        throwFatalError,
        throwToast,
        returnToast,
        catch: catch_,
    };
};
